import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { CatalogPublication } from '@/features/publications-catalog/types';
import type { PortalPublication, PublicationRow } from './types';
import {
  boostRow,
  fetchAgencySettings,
  fetchCatalogEntries,
  fetchPortals,
  purgeStalePosts,
  removeRow,
  retryRow,
} from './use-publications-actions';
import { usePublicationEditing } from './use-publication-editing';
import type { AdErrorInfo, AdSpendSummary } from '../ad-dialogs/ad-dialog-types';
import { NOTHING_CONFIGURED, type AdReadiness } from '@/shared/utils/ad-readiness';

type Translate = (key: string) => string;

/**
 * Resolves which image list retryRow's Instagram-images gate should check:
 * the fetched S3 gallery when available, else the raw property.media.images
 * refs. media.images can be empty while real images still live in storage
 * (crawled/legacy listings) -- the same gap PublishDialog already accounts
 * for via its own usePropertyImages fetch. The caller (PublicationsCenter)
 * runs that same fetch and passes the resolved list in as `propertyImages`
 * -- kept out of this hook so the isolated fetch-loop regression test
 * doesn't have to pull in usePropertyImages' full service/i18n import chain.
 */
export function resolveRetryImages(propertyImages: string[], fetchedImageUrls: string[]): string[] {
  return fetchedImageUrls.length > 0 ? fetchedImageUrls : propertyImages;
}

/**
 * Purges stale meta posts once per session and refreshes live + catalog data.
 */
async function maybePurgeStale(
  agencyId: string,
  propertyId: string,
  portals: Record<string, PortalPublication>,
  setPublications: (p: Record<string, PortalPublication>) => void,
  setCatalogEntries: (entries: CatalogPublication[]) => void,
  t: Translate,
) {
  const didPurge = await purgeStalePosts(agencyId, propertyId, portals);
  if (!didPurge) return;
  const [refreshed, catalog] = await Promise.all([
    fetchPortals(agencyId, propertyId),
    fetchCatalogEntries(agencyId),
  ]);
  setPublications(refreshed);
  setCatalogEntries(catalog);
  toast.success(t('publications.toasts.stalePostsRemoved'));
}

/**
 * Loads publish status, agency settings, and catalog index once per identity.
 * Setters stay out of deps to avoid recreating fetchData every render.
 */
function useFetchData(propertyId: string, agencyId: string, t: Translate) {
  const [publications, setPublications] = useState<Record<string, PortalPublication>>({});
  const [catalogEntries, setCatalogEntries] = useState<CatalogPublication[]>([]);
  const [connectedPortals, setConnectedPortals] = useState<string[]>([]);
  const [publishablePortals, setPublishablePortals] = useState<string[]>([]);
  const [fbHasPaymentMethod, setFbHasPaymentMethod] = useState(false);
  const [fbTokenExpiresAt, setFbTokenExpiresAt] = useState<string | null>(null);
  const [igTokenExpiresAt, setIgTokenExpiresAt] = useState<string | null>(null);
  const [fbAdAccountId, setFbAdAccountId] = useState<string | null>(null);
  // The agency's own saved amounts; 0 until loaded or when it has saved none (no built-in amount).
  const [fbLeadAdBudget, setFbLeadAdBudget] = useState(0);
  const [fbLeadAdDuration, setFbLeadAdDuration] = useState(0);
  const [igLeadAdBudget, setIgLeadAdBudget] = useState(0);
  const [igLeadAdDuration, setIgLeadAdDuration] = useState(0);
  const [adReadiness, setAdReadiness] = useState<AdReadiness>(NOTHING_CONFIGURED);
  const [loading, setLoading] = useState(true);
  const purgeDoneRef = useRef(false);
  const tRef = useRef(t);
  tRef.current = t;

  const fetchData = useCallback(async () => {
    try {
      const [portals, settings, catalog] = await Promise.all([
        fetchPortals(agencyId, propertyId),
        fetchAgencySettings(agencyId),
        fetchCatalogEntries(agencyId),
      ]);
      setPublications(portals);
      setCatalogEntries(catalog);
      setConnectedPortals(settings.connectedPortals);
      setPublishablePortals(settings.publishablePortals);
      setFbHasPaymentMethod(settings.fbHasPaymentMethod);
      setFbTokenExpiresAt(settings.fbTokenExpiresAt);
      setIgTokenExpiresAt(settings.igTokenExpiresAt);
      setFbAdAccountId(settings.fbAdAccountId);
      setFbLeadAdBudget(settings.fbLeadAdBudget);
      setFbLeadAdDuration(settings.fbLeadAdDuration);
      setIgLeadAdBudget(settings.igLeadAdBudget);
      setIgLeadAdDuration(settings.igLeadAdDuration);
      setAdReadiness(settings.adReadiness);
      if (!purgeDoneRef.current && settings.connectedPortals.includes('facebook')) {
        purgeDoneRef.current = true;
        await maybePurgeStale(
          agencyId,
          propertyId,
          portals,
          setPublications,
          setCatalogEntries,
          (key) => tRef.current(key),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId, agencyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return {
    publications,
    catalogEntries,
    connectedPortals,
    publishablePortals,
    fbHasPaymentMethod,
    fbTokenExpiresAt,
    igTokenExpiresAt,
    fbAdAccountId,
    fbLeadAdBudget,
    fbLeadAdDuration,
    igLeadAdBudget,
    igLeadAdDuration,
    adReadiness,
    loading,
    fetchData,
  };
}

/**
 * Real estate publications hook: fetches publish status, and exposes remove/edit/save
 * handlers plus the edit-panel state for a property's portal publications.
 */
export function usePublications(
  propertyId: string,
  agencyId: string,
  propertyImages: string[] = [],
  opts?: {
    onSaveSuccess?: () => void;
    retryImages?: string[];
    /** Asks the user to confirm the spend before a boost is created; resolves false to cancel. */
    confirmSpend?: (summary: AdSpendSummary) => Promise<boolean>;
    /** Opens the dialog explaining why a boost was not created. */
    showAdError?: (error: AdErrorInfo) => void;
  },
) {
  const { t } = useTranslation('features/properties');
  const [removing, setRemoving] = useState<string | null>(null);
  const [boostingRow, setBoostingRow] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const fetch = useFetchData(propertyId, agencyId, t);
  const { fetchData } = fetch;
  const tRef = useRef(t);
  tRef.current = t;
  // retryImages (when the caller supplies it) is the fetched S3 gallery
  // resolved via resolveRetryImages -- see that function's docstring. Falls
  // back to propertyImages so callers that don't pass it keep prior behavior.
  const retryImages = opts?.retryImages ?? propertyImages;
  const imagesRef = useRef(retryImages);
  imagesRef.current = retryImages;

  const handleRemove = useCallback(async (row: PublicationRow) => {
    setRemoving(row.key);
    await removeRow(row, {
      agencyId,
      propertyId,
      t: (key) => tRef.current(key),
      refresh: fetchData,
    });
    setRemoving(null);
  }, [propertyId, agencyId, fetchData]);

  const confirmSpendRef = useRef(opts?.confirmSpend);
  confirmSpendRef.current = opts?.confirmSpend;
  const showAdErrorRef = useRef(opts?.showAdError);
  showAdErrorRef.current = opts?.showAdError;

  const handleBoost = useCallback(async (
    row: PublicationRow,
    budgetDailyCents: number,
    durationDays: number,
  ) => {
    // Fail closed: a boost spends the user's money, so without an explicit
    // confirmation (or when no confirmation dialog is wired) nothing is created.
    const accepted = await confirmSpendRef.current?.({
      kind: 'boost',
      items: [{ label: 'boost', dailyBudgetCents: budgetDailyCents, durationDays }],
    });
    if (!accepted) return;
    setBoostingRow(row.key);
    await boostRow(row, budgetDailyCents, durationDays, {
      agencyId,
      t: (key) => tRef.current(key),
      refresh: fetchData,
      showAdError: (error) => showAdErrorRef.current?.(error),
    });
    setBoostingRow(null);
  }, [agencyId, fetchData]);

  const handleRetry = useCallback(async (row: PublicationRow) => {
    setRetrying(row.key);
    await retryRow(row, {
      agencyId,
      propertyId,
      propertyImages: imagesRef.current,
      t: (key) => tRef.current(key),
      refresh: fetchData,
    });
    setRetrying(null);
  }, [agencyId, propertyId, fetchData]);

  const editing = usePublicationEditing({
    agencyId,
    propertyId,
    propertyImages,
    t,
    refresh: fetchData,
    onSaveSuccess: opts?.onSaveSuccess,
  });

  return {
    t,
    publications: fetch.publications,
    catalogEntries: fetch.catalogEntries,
    connectedPortals: fetch.connectedPortals,
    publishablePortals: fetch.publishablePortals,
    fbHasPaymentMethod: fetch.fbHasPaymentMethod,
    fbTokenExpiresAt: fetch.fbTokenExpiresAt,
    igTokenExpiresAt: fetch.igTokenExpiresAt,
    fbAdAccountId: fetch.fbAdAccountId,
    fbLeadAdBudget: fetch.fbLeadAdBudget,
    fbLeadAdDuration: fetch.fbLeadAdDuration,
    igLeadAdBudget: fetch.igLeadAdBudget,
    igLeadAdDuration: fetch.igLeadAdDuration,
    adReadiness: fetch.adReadiness,
    loading: fetch.loading,
    removing,
    boostingRow,
    retrying,
    fetchData,
    handleRemove,
    handleBoost,
    handleRetry,
    ...editing,
  };
}
